import { CssBaseline, ThemeProvider } from "@mui/material";
import type { ReactNode } from "react";
import { useEffect, useMemo } from "react";
import { useAppSelector } from "@/app/store";
import { createMuiThemeFromConfig } from "@/app/theme/createMuiThemeFromConfig";
import { useBrandColors, useResolvedThemeConfig } from "@/app/theme/useResolvedThemeConfig";
import { applyCssVariables, storeBrandColors } from "@/config/themes";
import { STATUS } from "@/constants";

type AppThemeProviderProps = {
  children: ReactNode;
};

export function AppThemeProvider({ children }: AppThemeProviderProps) {
  const themeConfig = useResolvedThemeConfig();
  const muiTheme = useMemo(() => createMuiThemeFromConfig(themeConfig), [themeConfig]);
  const brandColors = useBrandColors();
  const brandingLoaded = useAppSelector((state) => state.branding.status === STATUS.SUCCEEDED);

  useEffect(() => {
    applyCssVariables(themeConfig);
  }, [themeConfig]);

  // Only a loaded answer is remembered: until then `brandColors` is the stored value itself.
  useEffect(() => {
    if (brandingLoaded) {
      storeBrandColors(brandColors);
    }
  }, [brandingLoaded, brandColors]);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
